import {
  forwardRef,
  useRef,
  useCallback,
  type ForwardedRef,
  type CSSProperties,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  MoreVertical,
  Calendar,
  Trash2,
  Info,
  Plus,
  MoveHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { useConfirm } from '../../../../contexts/ConfirmContext';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import type {
  CourseAction,
  CustomCourseAction,
  CourseCardCourse,
} from '../hooks/useCourseCard';

export interface CourseDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  actions?: CourseAction[];
  onAction: (actionType: string, value?: unknown) => void;
  course: CourseCardCourse;
  customActions?: CustomCourseAction[];
  semester?: string;
}

const CourseDropdown = forwardRef<HTMLDivElement, CourseDropdownProps>(
  (
    { isOpen, onToggle, actions, onAction, course, customActions, semester },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const { confirm } = useConfirm();

    useEscapeKey(onToggle, isOpen);

    // Closes only when the click misses BOTH the trigger button and the
    // portal-positioned menu.
    useClickOutside([buttonRef, dropdownRef], onToggle, isOpen);

    const getDropdownStyle = useCallback((): CSSProperties => {
      if (!buttonRef.current) return { top: 0, right: 0 };
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 192;
      const dropdownHeight = 200;
      const padding = 8;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top: number;
      if (spaceBelow >= dropdownHeight + padding) {
        top = rect.bottom + 4;
      } else if (spaceAbove >= dropdownHeight + padding) {
        top = rect.top - dropdownHeight - 4;
      } else {
        top = Math.max(padding, window.innerHeight - dropdownHeight - padding);
      }

      let right = window.innerWidth - rect.right;

      if (rect.right - dropdownWidth < padding) {
        right = window.innerWidth - dropdownWidth - padding;
      }

      const maxWidth = Math.min(dropdownWidth, window.innerWidth - padding * 2);

      return {
        position: 'fixed',
        top,
        right: Math.max(padding, right),
        width: maxWidth,
        maxHeight: Math.min(dropdownHeight, window.innerHeight - padding * 2),
        zIndex: 9999,
      };
    }, []);

    const getActionIcon = (type: string): LucideIcon | null => {
      switch (type) {
        case 'semester':
          return Calendar;
        case 'add':
          return Plus;
        case 'remove':
          return Trash2;
        case 'details':
          return Info;
        case 'move':
          return MoveHorizontal;
        default:
          return null;
      }
    };

    const getActionLabel = (action: CourseAction): string => {
      switch (action.type) {
        case 'semester':
          return 'Add to Semester';
        case 'add':
          return 'Add to Plan';
        case 'remove':
          return semester === 'bookmarks' ? 'Remove from Bookmarks' : 'Remove from Plan';
        case 'details':
          return 'View Details';
        case 'move':
          return 'Move';
        default:
          return action.label || action.type;
      }
    };

    const handleActionClick = async (
      e: MouseEvent<HTMLButtonElement>,
      action: CourseAction | string,
      value: unknown = null
    ): Promise<void> => {
      e.stopPropagation();

      // Custom actions: action is a string identifier
      if (typeof action === 'string') {
        if (customActions && customActions.some((ca) => ca.action === action)) {
          onAction(action, value);
          onToggle();
          return;
        }
        return;
      }

      if (action.type === 'remove' && action.confirmable !== false) {
        const isBookmarks = semester === 'bookmarks';
        const confirmed = await confirm({
          title: isBookmarks ? 'Remove from Bookmarks' : 'Remove from Plan',
          message: isBookmarks
            ? `Remove ${course.code} from bookmarks?`
            : `Remove ${course.code} from your plan?`,
          confirmText: 'Remove',
          variant: 'danger',
        });

        if (confirmed) {
          onAction(action.type, action.value);
        }
      } else {
        onAction(action.type, action.value);
      }
      onToggle();
    };

    return (
      <div className="relative" ref={ref}>
        <button
          ref={buttonRef}
          onClick={onToggle}
          className="p-1 rounded hover:bg-black/10 transition-colors"
          aria-label="Course actions"
        >
          <MoreVertical className="w-4 h-4 text-gray-600" />
        </button>

        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              data-dropdown="true"
              className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 overflow-y-auto"
              style={getDropdownStyle()}
            >
              {customActions &&
                customActions.map((customAction, index) => {
                  const Icon = customAction.icon;
                  return (
                    <button
                      key={`custom-${index}`}
                      onClick={(e) => handleActionClick(e, customAction.action)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                        customAction.className || ''
                      }`}
                    >
                      {Icon && <Icon className="w-4 h-4 text-gray-500" />}
                      {customAction.label}
                    </button>
                  );
                })}

              {customActions && customActions.length > 0 && actions && actions.length > 0 && (
                <div className="border-t border-gray-200 my-1" />
              )}

              {actions &&
                actions.map((action, index) => {
                  const Icon = getActionIcon(action.type);
                  const label = getActionLabel(action);

                  return (
                    <button
                      key={`${action.type}-${index}`}
                      onClick={(e) => handleActionClick(e, action)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                    >
                      {Icon && <Icon className="w-4 h-4 text-gray-500" />}
                      {label}
                    </button>
                  );
                })}
            </div>,
            document.body
          )}
      </div>
    );
  }
);

CourseDropdown.displayName = 'CourseDropdown';

export default CourseDropdown;
